import * as THREE from "three";
import { FINISH } from "@/config/designer";
import type { GlossDab } from "@/core/finish/types";

export interface BrushUniforms {
  uBrushPos: { value: THREE.Vector3 };
  uBrushRadius: { value: number };
  uBrushActive: { value: number };
  uBrushTint: { value: THREE.Color };
  uGlossCount: { value: number };
  uGlossDabs: { value: THREE.Vector4[] };
}

export interface HoldMaterial {
  material: THREE.MeshStandardMaterial;
  uniforms: BrushUniforms;
}

/**
 * The foam blank's material: light sand with a soft clay-like studio response.
 *
 * Vertex colours carry the distance-field ambient occlusion computed at mesh
 * time, and a small shader injection paints the brush footprint directly onto
 * the surface. Drawing the brush on the geometry — rather than floating a
 * sphere in front of it — is the only way the preview can honestly show which
 * material a stroke will reach on a curved surface.
 */
export function createHoldMaterial(): HoldMaterial {
  const uniforms: BrushUniforms = {
    uBrushPos: { value: new THREE.Vector3(0, -1000, 0) },
    uBrushRadius: { value: 30 },
    uBrushActive: { value: 0 },
    uBrushTint: { value: new THREE.Color("#c8d7bc") },
    uGlossCount: { value: 0 },
    uGlossDabs: {
      value: Array.from({ length: FINISH.previewGlossDabs }, () => new THREE.Vector4(0, 0, 0, -1)),
    },
  };

  const material = new THREE.MeshStandardMaterial({
    color: new THREE.Color("#d8c5a3"),
    roughness: 0.86,
    metalness: 0.0,
    vertexColors: true,
    envMapIntensity: 0.48,
  });
  material.name = "foam";

  material.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);

    shader.vertexShader = shader.vertexShader
      .replace("#include <common>", "#include <common>\nvarying vec3 vEthosWorld;")
      .replace(
        "#include <project_vertex>",
        "#include <project_vertex>\nvEthosWorld = ( modelMatrix * vec4( transformed, 1.0 ) ).xyz;",
      );

    shader.fragmentShader = shader.fragmentShader
      .replace(
        "#include <common>",
        `#include <common>
varying vec3 vEthosWorld;
uniform vec3 uBrushPos;
uniform float uBrushRadius;
uniform float uBrushActive;
uniform vec3 uBrushTint;
uniform int uGlossCount;
uniform vec4 uGlossDabs[${FINISH.previewGlossDabs}];`,
      )
      .replace(
        "vec4 diffuseColor = vec4( diffuse, opacity );",
        `vec4 diffuseColor = vec4( diffuse, opacity );
float ethosGloss = 0.0;
for ( int ethosGlossIndex = 0; ethosGlossIndex < ${FINISH.previewGlossDabs}; ethosGlossIndex++ ) {
  if ( ethosGlossIndex >= uGlossCount ) break;
  vec4 ethosGlossDab = uGlossDabs[ethosGlossIndex];
  float ethosGlossDistance = distance( vEthosWorld, ethosGlossDab.xyz );
  ethosGloss = max( ethosGloss, 1.0 - smoothstep( ethosGlossDab.w * 0.82, ethosGlossDab.w, ethosGlossDistance ) );
}
vec3 ethosGlossColour = min( diffuseColor.rgb * 1.08 + vec3( 0.02 ), vec3( 1.0 ) );
diffuseColor.rgb = mix( diffuseColor.rgb, ethosGlossColour, ethosGloss * 0.22 );
if ( uBrushActive > 0.5 ) {
  float ethosD = distance( vEthosWorld, uBrushPos ) / max( uBrushRadius, 0.0001 );
  float ethosFill = 1.0 - smoothstep( 0.0, 1.0, ethosD );
  float ethosRing = smoothstep( 0.90, 0.985, ethosD ) * ( 1.0 - smoothstep( 0.985, 1.06, ethosD ) );
  diffuseColor.rgb = mix( diffuseColor.rgb, uBrushTint, ethosFill * 0.20 + ethosRing * 0.9 );
}`,
      );
    shader.fragmentShader = shader.fragmentShader.replace(
      "#include <roughnessmap_fragment>",
      "#include <roughnessmap_fragment>\nroughnessFactor = mix( roughnessFactor, 0.16, ethosGloss );",
    );
  };
  // Force a recompile whenever the injection changes across hot reloads.
  material.customProgramCacheKey = () => "ethos-foam-v5-colours";

  return { material, uniforms };
}

/** Upload an even preview sample; export always uses the complete dab list. */
export function updateGlossUniforms(uniforms: BrushUniforms, dabs: readonly GlossDab[]): void {
  const count = Math.min(dabs.length, FINISH.previewGlossDabs);
  uniforms.uGlossCount.value = count;
  const stride = dabs.length <= count ? 1 : dabs.length / count;
  for (let i = 0; i < FINISH.previewGlossDabs; i++) {
    const target = uniforms.uGlossDabs.value[i] as THREE.Vector4;
    if (i >= count) {
      target.set(0, 0, 0, -1);
      continue;
    }
    const dab = dabs[Math.min(dabs.length - 1, Math.floor(i * stride))] as GlossDab;
    target.set(dab.x, dab.y, dab.z, dab.radius);
  }
}
